import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Trophy, AlertCircle, Mail, TimerReset as KeyReset, Loader2, QrCode, X } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { supabase } from '../../lib/supabase';
import { QrScanner } from '@yudiel/react-qr-scanner';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [validationError, setValidationError] = useState('');
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resetPasswordSent, setResetPasswordSent] = useState(false);
  const [sendingResetEmail, setSendingResetEmail] = useState(false);
  const [schoolLogo, setSchoolLogo] = useState<string | null>(null);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [showScanner, setShowScanner] = useState(false);
  
  const { t } = useTranslation('common');
  const { login, user, loading, error, loginWithToken } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Check for token in URL
    const token = searchParams.get('token');
    if (token) {
      handleTokenLogin(token);
    }
    
    // Fetch school logo from system settings
    const fetchSchoolLogo = async () => {
      try {
        const { data, error } = await supabase
          .from('system_settings')
          .select('setting_value')
          .eq('setting_name', 'school_logo')
          .maybeSingle();
        
        if (error) {
          console.error('Error fetching school logo:', error);
          return;
        }
        
        if (data?.setting_value?.url) {
          setSchoolLogo(data.setting_value.url);
        }
      } catch (error) {
        console.error('Error fetching school logo:', error);
      }
    };
    
    fetchSchoolLogo();
  }, [searchParams]);

  const handleTokenLogin = async (token: string) => {
    setTokenLoading(true);
    setTokenError(null);
    
    try {
      await loginWithToken(token);
      // The redirect will happen automatically after successful login
    } catch (err: any) {
      console.error('Token login error:', err);
      setTokenError(err.message || 'Failed to login with token');
    } finally {
      setTokenLoading(false);
    }
  };

  const handleQrCodeScan = (result: string) => {
    // Check if the result is a token
    if (result && result.length > 10) {
      // Close the scanner
      setShowScanner(false);
      
      // Extract token if it's a URL with a token parameter
      let token = result;
      try {
        const url = new URL(result);
        const tokenParam = url.searchParams.get('token');
        if (tokenParam) {
          token = tokenParam;
        }
      } catch (e) {
        // Not a URL, use the raw result as token
      }
      
      // Process the token
      handleTokenLogin(token);
    }
  };

  const handleScanError = (error: Error) => {
    console.error('QR Scan Error:', error);
    setTokenError('Failed to scan QR code. Please try again or enter your credentials manually.');
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleResetPassword = async () => {
    if (!email || !validateEmail(email)) {
      setValidationError('Please enter a valid email address');
      return;
    }

    try {
      setSendingResetEmail(true);
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;
      
      setResetPasswordSent(true);
      setValidationError('');
    } catch (err) {
      setValidationError('Failed to send password reset email. Please try again.');
    } finally {
      setSendingResetEmail(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!email || !validateEmail(email)) {
      setValidationError('Please enter a valid email address');
      return;
    }

    try {
      setResendingEmail(true);
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email.trim(),
      });

      if (error) throw error;
      
      setResendSuccess(true);
      setValidationError('');
    } catch (err) {
      setValidationError('Failed to resend confirmation email. Please try again.');
    } finally {
      setResendingEmail(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');
    setResendSuccess(false);
    setResetPasswordSent(false);

    // Enhanced validation
    if (!email.trim()) {
      setValidationError('Email address is required');
      return;
    }

    if (!validateEmail(email)) {
      setValidationError('Please enter a valid email address');
      return;
    }

    if (!password) {
      setValidationError('Password is required');
      return;
    }

    if (password.length < 6) {
      setValidationError('Password must be at least 6 characters long');
      return;
    }

    try {
      await login(email.trim(), password);
    } catch (err) {
      // Error is handled by the auth store
      console.error('Login failed:', err);
    }
  };

  if (user) {
    navigate('/');
    return null;
  }

  if (tokenLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-md">
          <div className="text-center">
            <Loader2 className="h-12 w-12 text-blue-600 animate-spin mx-auto" />
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Processing Login
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Please wait while we log you in...
            </p>
          </div>
        </div>
      </div>
    );
  }

  const getErrorMessage = (error: string) => {
    if (error.includes('Invalid login credentials')) {
      return (
        <div className="space-y-2">
          <p>Invalid email or password. Please try again.</p>
          <button
            onClick={handleResetPassword}
            disabled={sendingResetEmail}
            className="inline-flex items-center text-sm text-blue-600 hover:text-blue-500"
          >
            <KeyReset className="h-4 w-4 mr-1" />
            {sendingResetEmail ? 'Sending reset email...' : 'Forgot your password?'}
          </button>
        </div>
      );
    }
    if (error.includes('Email not confirmed')) {
      return (
        <div className="space-y-2">
          <p>Please verify your email address before logging in.</p>
          <p className="text-sm">
            Check your inbox (and spam folder) for the confirmation email.
          </p>
          <button
            onClick={handleResendConfirmation}
            disabled={resendingEmail}
            className="inline-flex items-center text-sm text-blue-600 hover:text-blue-500"
          >
            <Mail className="h-4 w-4 mr-1" />
            {resendingEmail ? 'Sending...' : 'Resend confirmation email'}
          </button>
        </div>
      );
    }
    if (error.includes('Too many requests')) {
      return 'Too many login attempts. Please try again later.';
    }
    if (error.includes('Token login failed')) {
      return 'The login link you used is invalid or has expired. Please try again or contact an administrator.';
    }
    return 'An error occurred during login. Please try again.';
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {schoolLogo ? (
            <div className="flex justify-center">
              <img 
                src={schoolLogo} 
                alt="School Logo" 
                className="h-24 w-auto object-contain"
              />
            </div>
          ) : (
            <div className="flex justify-center">
              <Trophy className="h-12 w-12 text-blue-600" />
            </div>
          )}
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Sign in to Annai Sports Management
          </h2>
        </div>
        
        {resetPasswordSent && (
          <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
            <div className="flex">
              <Mail className="h-5 w-5 text-green-400" />
              <div className="ml-3">
                <p className="text-sm text-green-700">
                  Password reset email has been sent. Please check your inbox.
                </p>
              </div>
            </div>
          </div>
        )}

        {resendSuccess && (
          <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
            <div className="flex">
              <Mail className="h-5 w-5 text-green-400" />
              <div className="ml-3">
                <p className="text-sm text-green-700">
                  Confirmation email has been resent. Please check your inbox.
                </p>
              </div>
            </div>
          </div>
        )}

        {(error || validationError || tokenError) && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                {typeof (validationError || tokenError || getErrorMessage(error)) === 'string' ? (
                  <p className="text-sm text-red-700">
                    {validationError || tokenError || getErrorMessage(error)}
                  </p>
                ) : (
                  validationError || tokenError || getErrorMessage(error)
                )}
              </div>
            </div>
          </div>
        )}
        
        {showScanner ? (
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Scan QR Code to Login</h3>
              <button 
                onClick={() => setShowScanner(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="relative">
              <QrScanner
                onDecode={handleQrCodeScan}
                onError={handleScanError}
                containerStyle={{ borderRadius: '0.5rem' }}
              />
            </div>
            <p className="mt-4 text-sm text-gray-600 text-center">
              Position the QR code within the frame to scan
            </p>
          </div>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="email" className="sr-only">Email address</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={(e) => setEmail(e.target.value.trim())}
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Email address"
                />
              </div>
              <div>
                <label htmlFor="password" className="sr-only">Password</label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Password"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm">
                <button
                  type="button"
                  onClick={handleResetPassword}
                  disabled={sendingResetEmail}
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  Forgot your password?
                </button>
              </div>
            </div>

            <div className="flex flex-col space-y-3">
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400"
              >
                {loading ? (
                  <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                    <div className="h-5 w-5 border-t-2 border-white border-solid rounded-full animate-spin"></div>
                  </span>
                ) : null}
                Sign in
              </button>
              
              <button
                type="button"
                onClick={() => setShowScanner(true)}
                className="group relative w-full flex justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <QrCode className="h-5 w-5 mr-2" />
                Sign in with QR Code
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login;